/**
 * Where a question comes from, in the consultant's conversation (2026-09-28; lib/copilot/ask): the chip above the
 * composer that says what the next message is about ("Asking about: Day brief, 18 Aug 2026: Sonic"), the muted line
 * under a person's message that carried a context, and the quick questions the consultant's page offers while the
 * composer is empty. Pure: the container (imports-copilot.tsx) holds the state.
 */
import { MessageSquareText, X } from "lucide-react";
import { ABOUT_QUOTE_MAX, quoteWasCut, type CopilotAbout } from "@/lib/copilot/ask";
import type { QuickAsk } from "@/lib/brief/ask";

/**
 * "Asking about: <label>", with a button that clears it: the next message then goes without a context. A question about
 * a selection shows the passage under it, so the person sees what the question carries (the page's words, as text),
 * and says so when it was cut to fit.
 */
export function ContextChip({ about, onClear, disabled = false }: { about: CopilotAbout; onClear: () => void; disabled?: boolean }) {
  const chip = (
    <div data-context-chip className="flex min-w-0 items-center gap-1 self-start rounded-full border border-brand/40 bg-brand/5 py-0.5 pl-3 pr-1 text-xs text-on-surface">
      <span className="min-w-0 truncate">
        <span className="text-on-surface-muted">Asking about: </span>
        {about.label}
      </span>
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={`Clear: ask without "${about.label}"`}
        title="Ask without it"
        className="flex size-5 shrink-0 items-center justify-center rounded-full text-on-surface-muted transition-colors hover:bg-surface-muted hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <X className="size-3" />
      </button>
    </div>
  );
  if (!about.quote) return chip;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {chip}
      <p
        data-context-quote
        title={about.quote}
        className="line-clamp-2 break-words border-l-2 border-brand/40 pl-2 text-xs italic text-on-surface-muted"
      >
        {`“${about.quote}”`}
      </p>
      {quoteWasCut(about.quote) && (
        <p data-context-cut className="text-[11px] text-on-surface-muted">
          {`Your selection was cut to ${ABOUT_QUOTE_MAX} characters.`}
        </p>
      )}
    </div>
  );
}

/** Under a person's message that carried a context: what it was about. */
export function AboutLine({ about }: { about: CopilotAbout | null }) {
  if (!about) return null;
  return <p className="mt-1 break-words text-xs text-on-surface-muted">{`About: ${about.label}`}</p>;
}

/** Quick questions while the composer is empty: a click drafts one and sets its context; nothing is sent. */
export function QuickAsks({ asks, disabled, onPick }: { asks: QuickAsk[]; disabled: boolean; onPick: (ask: QuickAsk) => void }) {
  if (asks.length === 0) return null;
  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      <p id="copilot-quick-label" className="text-xs text-on-surface-muted">
        Quick questions:
      </p>
      <div role="group" aria-labelledby="copilot-quick-label" className="flex flex-wrap gap-1.5">
        {asks.map((a) => (
          <button
            key={a.text}
            type="button"
            disabled={disabled}
            onClick={() => onPick(a)}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-0.5 text-[11px] font-medium text-on-surface transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            <MessageSquareText aria-hidden className="size-3 text-on-surface-muted" />
            {a.text}
          </button>
        ))}
      </div>
    </div>
  );
}
